<template>
  <div class="function-table-container">
    <!-- Data Table -->
    <div class="table-wrapper">
      <el-table
        ref="tableRef"
        :key="selectedColumns.join(',')"
        :data="paginatedData"
        stripe
        border
        :height="tableHeight"
        style="width: 100%"
        :default-sort="{ prop: 'gene_name', order: 'ascending' }"
        @sort-change="handleSortChange"
      >
        <!-- Dynamic columns based on selection -->
        <el-table-column
          v-for="col in visibleColumns"
          :key="col.key"
          :prop="col.key"
          :label="col.label"
          :width="col.width"
          :min-width="col.minWidth"
          :fixed="col.fixed"
          sortable="custom"
          show-overflow-tooltip
        >
        </el-table-column>

        <el-table-column
          label="Actions"
          width="180"
          align="center"
          fixed="right"
        >
          <template #default="{ row }">
            <el-button size="mini" @click="showDetails(row)">
              Show All
            </el-button>
            <el-button size="mini" @click="copyRow(row)">
              Copy
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Pagination -->
    <div class="pagination-bar">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      >
      </el-pagination>
    </div>

    <!-- Details Dialog -->
    <el-dialog
      :title="`Protein Details - ${selectedRow ? selectedRow.gene_name : ''}`"
      :visible.sync="detailsVisible"
      width="70%"
    >
      <div v-if="selectedRow" class="details-grid">
        <div class="detail-item">
          <span class="detail-label">Protein Name:</span>
          <span class="detail-value">{{ selectedRow.gene_name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Protein Accession:</span>
          <span class="detail-value">{{ selectedRow.protein_accession }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Protein Description:</span>
          <span class="detail-value">{{ selectedRow.protein_description }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KOG Category:</span>
          <span class="detail-value">{{ selectedRow.kog_category }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KOG NO.:</span>
          <span class="detail-value">{{ selectedRow.kog_no }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KOG Description:</span>
          <span class="detail-value">{{ selectedRow.kog_description }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Biological Process:</span>
          <span class="detail-value">{{ selectedRow.biological_process }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Cellular Component:</span>
          <span class="detail-value">{{ selectedRow.cellular_component }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Molecular Function:</span>
          <span class="detail-value">{{ selectedRow.molecular_function }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Pfam ID:</span>
          <span class="detail-value">{{ selectedRow.pfam_id }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Domain Description:</span>
          <span class="detail-value">{{ selectedRow.domain_description }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Subcellular Localization:</span>
          <span class="detail-value">{{ selectedRow.subcellular_localization }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KEGG KO No.:</span>
          <span class="detail-value">{{ selectedRow.kegg_ko_no }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KEGG Gene:</span>
          <span class="detail-value">{{ selectedRow.kegg_gene }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">KEGG Pathway:</span>
          <span class="detail-value">{{ selectedRow.kegg_pathway }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">HallMark:</span>
          <span class="detail-value">{{ selectedRow.hallmark }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">MSigDB URL:</span>
          <span class="detail-value">{{ selectedRow.msigdburl }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Reactome Pathway:</span>
          <span class="detail-value">{{ selectedRow.reactome_pathway }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Reactome Link:</span>
          <span class="detail-value">{{ selectedRow.reactome_link }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">WikiPathways:</span>
          <span class="detail-value">{{ selectedRow.wikipathways }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">External Details URL:</span>
          <span class="detail-value">{{ selectedRow.externaldetailsurl }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">IS_TF:</span>
          <span class="detail-value">{{ selectedRow.is_tf }}</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'GeneFunctionTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: [Number, String],
      default: '100%'
    },
    selectedColumns: {
      type: Array,
      default: () => ['gene_name', 'protein_description', 'biological_process', 'kegg_pathway']
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 20,
      sortProp: 'gene_name',
      sortOrder: 'ascending',
      detailsVisible: false,
      selectedRow: null
    }
  },
  mounted() {
    this.doTableLayout()
    window.addEventListener('resize', this.doTableLayout)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.doTableLayout)
  },
  computed: {
    // Column definitions with visibility control
    columnDefinitions() {
      return [
        { key: 'gene_name', label: 'Protein Name', width: '140', fixed: 'left' },
        { key: 'protein_accession', label: 'Protein Accession', width: '160' },
        { key: 'protein_description', label: 'Protein Description', minWidth: '250' },
        { key: 'kog_category', label: 'KOG Category', width: '140' },
        { key: 'kog_no', label: 'KOG NO.', width: '120' },
        { key: 'kog_description', label: 'KOG Description', minWidth: '220' },
        { key: 'biological_process', label: 'Biological Process', minWidth: '220' },
        { key: 'cellular_component', label: 'Cellular Component', minWidth: '200' },
        { key: 'molecular_function', label: 'Molecular Function', minWidth: '220' },
        { key: 'pfam_id', label: 'Pfam ID', width: '120' },
        { key: 'domain_description', label: 'Domain Description', minWidth: '200' },
        { key: 'subcellular_localization', label: 'Subcellular Localization', minWidth: '200' },
        { key: 'kegg_ko_no', label: 'KEGG KO No.', width: '140' },
        { key: 'kegg_gene', label: 'KEGG Gene', width: '140' },
        { key: 'kegg_pathway', label: 'KEGG Pathway', minWidth: '220' },
        { key: 'hallmark', label: 'HallMark', width: '140' },
        { key: 'msigdburl', label: 'MSigDB URL', minWidth: '180' },
        { key: 'reactome_pathway', label: 'Reactome Pathway', minWidth: '220' },
        { key: 'reactome_link', label: 'Reactome Link', minWidth: '180' },
        { key: 'wikipathways', label: 'WikiPathways', minWidth: '180' },
        { key: 'externaldetailsurl', label: 'External Details URL', minWidth: '200' },
        { key: 'is_tf', label: 'IS_TF', width: '100' }
      ]
    },
    visibleColumns() {
      // Always show gene_name first, then other selected columns
      const columns = this.columnDefinitions.filter(col => 
        this.selectedColumns.includes(col.key)
      )
      
      // Ensure gene_name is always first
      const geneNameCol = columns.find(col => col.key === 'gene_name')
      const otherCols = columns.filter(col => col.key !== 'gene_name')
      
      return geneNameCol ? [geneNameCol, ...otherCols] : columns
    },
    // Sort data
    sortedData() {
      if (!this.data || this.data.length === 0) {
        return []
      }
      
      const data = [...this.data]
      
      if (this.sortProp) {
        data.sort((a, b) => {
          let aVal = a[this.sortProp]
          let bVal = b[this.sortProp]
          
          // Handle string sorting
          aVal = String(aVal || '')
          bVal = String(bVal || '')
          
          if (this.sortOrder === 'ascending') {
            return aVal.localeCompare(bVal)
          } else {
            return bVal.localeCompare(aVal)
          }
        })
      }
      
      return data
    },
    
    // Paginate data
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.sortedData.slice(start, end)
    },
    
    total() {
      return this.sortedData.length
    }
  },
  watch: {
    selectedColumns: {
      handler() {
        this.doTableLayout()
      },
      deep: true
    },
    data() {
      this.doTableLayout()
    }
  },
  methods: {
    doTableLayout() {
      this.$nextTick(() => {
        if (this.$refs.tableRef && this.$refs.tableRef.doLayout) {
          this.$refs.tableRef.doLayout()
        }
      })
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.doTableLayout()
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
      this.doTableLayout()
    },
    
    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      this.currentPage = 1
      this.doTableLayout()
    },
    
    showDetails(row) {
      this.selectedRow = row
      this.detailsVisible = true
    },
    
    copyRow(row) {
      const fields = [
        'gene_name', 'protein_accession', 'protein_description', 'kog_category',
        'kog_no', 'kog_description', 'biological_process', 'cellular_component',
        'molecular_function', 'pfam_id', 'domain_description', 'subcellular_localization',
        'kegg_ko_no', 'kegg_gene', 'kegg_pathway', 'hallmark', 'msigdburl',
        'reactome_pathway', 'reactome_link', 'wikipathways', 'externaldetailsurl', 'is_tf'
      ]
      
      const values = fields.map(f => row[f] || '').join('\t')
      
      // Copy to clipboard
      const textarea = document.createElement('textarea')
      textarea.value = values
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      
      this.$notify({
        title: 'Success',
        message: 'Row copied to clipboard',
        type: 'success',
        position: 'top-right'
      })
    }
  }
}
</script>

<style scoped>
.function-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  padding: 8px;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.details-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.detail-label {
  font-weight: 600;
  color: #374151;
  min-width: 200px;
  flex-shrink: 0;
}

.detail-value {
  color: #6b7280;
  word-break: break-word;
}

/* Table styling enhancements */
::v-deep .el-table th {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  font-weight: 600;
  font-size: 13px;
}

::v-deep .el-table td {
  font-size: 13px;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafafa;
}

::v-deep .el-table__body tr:hover > td {
  background-color: #f0f7ff !important;
}
</style>

